import * as React from "react";
import { Sidebar, SIDEBAR_COLLAPSED_STORAGE_KEY } from "./sidebar.js";
import { MobileNav } from "./mobile-nav.js";
import { Header } from "./header.js";
import type { HeaderSearch } from "./header.js";
import { Footer } from "./footer.js";
import type { NavGroup } from "../navigation/nav-types.js";
import type { UserMenuProps } from "./user-menu.js";
import { TooltipProvider } from "../ui/tooltip.js";

function readStoredCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export interface AppShellProps {
  navGroups: NavGroup[];
  user: UserMenuProps;
  environment?: string;
  version?: string;
  /** School/campus/academic-year `ScopeSelector`s rendered in the header. */
  scopeSelectors?: React.ReactNode;
  /** Passed to `Header` — see `HeaderProps.search` / `.notifications`. */
  search?: HeaderSearch;
  notifications?: React.ReactNode;
  /** In-app Help page, linked from the header and footer (KG-13). */
  helpHref?: string;
  children: React.ReactNode;
}

/**
 * The mandatory application shell — header, grouped/collapsible sidebar, main content region,
 * and quiet footer — per `17_UI_VISUAL_DESIGN_SYSTEM.md` §2.
 */
export function AppShell({ navGroups, user, environment, version, scopeSelectors, search, notifications, helpHref, children }: AppShellProps) {
  const [collapsed, setCollapsed] = React.useState(readStoredCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const handleCollapsedChange = React.useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
    } catch {
      // Ignore write failures; the in-memory state still applies for this session.
    }
  }, []);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex h-dvh overflow-hidden bg-background">
        <Sidebar groups={navGroups} collapsed={collapsed} onCollapsedChange={handleCollapsedChange} />
        <MobileNav groups={navGroups} open={mobileNavOpen} onOpenChange={setMobileNavOpen} scopeSelectors={scopeSelectors} />

        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            onOpenMobileNav={() => setMobileNavOpen(true)}
            user={user}
            scopeSelectors={scopeSelectors}
            {...(search ? { search } : {})}
            {...(notifications ? { notifications } : {})}
            {...(helpHref ? { helpHref } : {})}
          />
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">{children}</div>
          </main>
          <Footer environment={environment} version={version} helpHref={helpHref} />
        </div>
      </div>
    </TooltipProvider>
  );
}
